import { useEffect, useState, useRef, useMemo, useCallback, useId } from 'react';
import { useParams } from 'react-router-dom';
import { Product } from '../../../types/common';
import LoadMore from '../../UI/LoadMore/LoadMore';
import CatalogDescription from '../CatalogDescription/CatalogDescription';
import ProductGridColumnsToggle, { type ProductGridColumnsValue } from '../ProductGridColumnsToggle/ProductGridColumnsToggle';
import ProductCard, { type ProductCardData, type Discount, type ImagesObject } from '../../shared/ProductCard/ProductCard';
import styles from './ProductCardList.module.css';

type SelectedFilterChip = { key: string; label: string; onRemove: () => void };
interface IProductCardListProps {
    isMobile?: boolean;
    products: Product[];
    currentSeason?: 'all' | 'summer' | 'autumn' | 'winter' | 'spring';
    isWishlist?: boolean;
    selectedFilterChips?: SelectedFilterChip[];
}
type LayoutMode = 'desktop' | 'tablet' | 'mobile';

const PRODUCT_LIST_LIMIT = 12;
const BREAKPOINT_TABLET = 1024;
const BREAKPOINT_MOBILE = 650;

const getLayoutMode = (w: number): LayoutMode => {
    if (w <= BREAKPOINT_MOBILE) return 'mobile';
    if (w <= BREAKPOINT_TABLET) return 'tablet';
    return 'desktop';
};
const OPTIONS_BY_MODE: Record<LayoutMode, readonly ProductGridColumnsValue[]> = {
    desktop: [3, 4],
    tablet: [3, 2],
    mobile: [2, 1],
};
const DEFAULT_BY_MODE: Record<LayoutMode, ProductGridColumnsValue> = {
    desktop: 3,
    tablet: 3,
    mobile: 2,
};

const GRID_COLUMNS_STORAGE_KEY = 'productCardList:gridColumns:v1';
type GridColumnsPrefs = Partial<Record<LayoutMode, ProductGridColumnsValue>>;
const isGridColumnsValue = (v: unknown): v is ProductGridColumnsValue => v === 1 || v === 2 || v === 3 || v === 4;
const readGridColumnsPrefs = (): GridColumnsPrefs => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage.getItem(GRID_COLUMNS_STORAGE_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};

        const obj = parsed as Record<string, unknown>;
        const prefs: GridColumnsPrefs = {};
        (['desktop', 'tablet', 'mobile'] as const).forEach((mode) => {
            const v = obj[mode];
            if (isGridColumnsValue(v)) prefs[mode] = v;
        });
        return prefs;
    } catch {
        return {};
    }
};
const getStoredColumnsForMode = (mode: LayoutMode): ProductGridColumnsValue | null => {
    const prefs = readGridColumnsPrefs();
    const stored = prefs[mode];
    const allowed = OPTIONS_BY_MODE[mode];
    if (stored !== undefined && allowed.includes(stored)) return stored;
    return null;
};
const writeColumnsForMode = (mode: LayoutMode, value: ProductGridColumnsValue) => {
    if (typeof window === 'undefined') return;
    try {
        const current = readGridColumnsPrefs();
        const next: GridColumnsPrefs = { ...current, [mode]: value };
        window.localStorage.setItem(GRID_COLUMNS_STORAGE_KEY, JSON.stringify(next));
    } catch {
        // ignore
    }
};

const getEffectivePrice = (p: Product) => {
    if (p.specialDiscount) {
        return p.specialDiscount.discountedPrice;
    }
    if (p.discount) {
        return p.discount.discountedPrice;
    }
    return p.price;
};

const isPositiveNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;

const toImages = (p: Product): ImagesObject => ({
    image: p.image ?? '',
    secondImage: p.secondImage ?? '',
    thirdImage: p.thirdImage ?? '',
    fourthImage: p.fourthImage ?? '',
    fifthImage: p.fifthImage ?? '',
    sixthImage: p.sixthImage ?? '',
});

const computeDiscount = (p: Product): Discount => {
    const basePrice = isPositiveNumber(p.price) ? p.price : null;
    const raw = (p as any)?.specialDiscount ?? (p as any)?.discount;
    const discounted = raw && isPositiveNumber(raw.discountedPrice) ? raw.discountedPrice : null;
    if (!basePrice || !discounted || discounted >= basePrice) return null;
    const percent = Math.max(0, Math.min(100, Math.round(((basePrice - discounted) / basePrice) * 100)));
    return { percent, discountedPrice: discounted };
};

const isPromoKey = (v: unknown): v is 'promo' | 'season' | 'sale' | 'blank' => v === 'promo' || v === 'season' || v === 'sale' || v === 'blank';

const normalizePromoType = (p: Product): string => {
    const candidate = (p as any)?.promoType;
    return isPromoKey(candidate) ? candidate : 'blank';
};

const mapProductToCardData = (p: Product): ProductCardData => {
    const discount = computeDiscount(p);
    const images = toImages(p);
    return {
        _id: p.id ?? '',
        category: {
            id: p.category?.id ?? '',
            name: p.category?.name ?? '',
            url: p.category?.url ?? '',
        },
        discount,
        name: p.name ?? '',
        price: isPositiveNumber(p.price) ? p.price : 0,
        weight: isPositiveNumber((p as any)?.weight) ? (p as any).weight : 0,
        fillerType: {
            id: p.fillerType?.id ?? '',
            name: p.fillerType?.name ?? '',
            url: p.fillerType?.url ?? '',
        },
        fabricType: {
            id: p.fabricType?.id ?? '',
            name: p.fabricType?.name ?? '',
            url: p.fabricType?.url ?? '',
        },
        articleNumber: (p as any)?.articleNumber ?? '',
        brandName: (p as any)?.brandName ?? '',
        nameDescription: (p as any)?.nameDescription ?? '',
        priority: isPositiveNumber((p as any)?.priority) ? (p as any).priority : 0,
        images,
        group: {
            id: p.group?.id ?? '',
            name: p.group?.name ?? '',
            url: p.group?.url ?? '',
            optionValue: p.group?.optionValue ?? '',
        },
        slug: p.slug ?? '',
        season: (p as any)?.season ?? null,
        promoType: normalizePromoType(p),
    };
};

const ProductCardList: React.FC<IProductCardListProps> = ({ isMobile, products, currentSeason = 'all', isWishlist, selectedFilterChips = [] }) => {
    const { url } = useParams();
    const dropdownPanelId = useId();

    // const [productsToRender, setProductsToRender] = useState<Product[]>([]);
    const [count, setCount] = useState(PRODUCT_LIST_LIMIT);
    const [sortOption, setSortOption] = useState<'popular' | 'expensive' | 'cheap'>('popular');
    const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => {
        const w = typeof window !== 'undefined' ? window.innerWidth : 1440;
        return getLayoutMode(w);
    });
    const [gridColumns, setGridColumns] = useState<ProductGridColumnsValue>(() => {
        const w = typeof window !== 'undefined' ? window.innerWidth : 1440;
        const mode = getLayoutMode(w);
        const stored = getStoredColumnsForMode(mode);
        return stored ?? DEFAULT_BY_MODE[mode];
    });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRootRef = useRef<HTMLDivElement | null>(null);
    const toggleRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onResize = () => {
            const next = getLayoutMode(window.innerWidth);
            setLayoutMode((prev) => (prev === next ? prev : next));
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        const stored = getStoredColumnsForMode(layoutMode);
        const allowed = OPTIONS_BY_MODE[layoutMode];
        setGridColumns((prev) => {
            if (stored !== null) return stored;
            if (allowed.includes(prev)) return prev;
            return DEFAULT_BY_MODE[layoutMode];
        });
    }, [layoutMode]);
    useEffect(() => {
        writeColumnsForMode(layoutMode, gridColumns);
    }, [layoutMode, gridColumns]);

    const toggleOptions = useMemo(() => OPTIONS_BY_MODE[layoutMode], [layoutMode]);

    const groupedProducts = useMemo(() => {
        const cheapestPerSeasonGroup = new Map<string, Product>();
        const cheapestPerGroupOverall = new Map<string, Product>();
        const unique: Product[] = [];

        products.forEach((p) => {
            const groupId = p.group?.id ?? null;
            const seasonKey = p.season ?? 'none';
            if (groupId) {
                const seasonGroupKey = `${seasonKey}_${groupId}`;
                const storedSeasonGroup = cheapestPerSeasonGroup.get(seasonGroupKey);
                if (!storedSeasonGroup || getEffectivePrice(p) < getEffectivePrice(storedSeasonGroup)) {
                    cheapestPerSeasonGroup.set(seasonGroupKey, p);
                }
                const storedGroup = cheapestPerGroupOverall.get(groupId);
                if (!storedGroup || getEffectivePrice(p) < getEffectivePrice(storedGroup)) {
                    cheapestPerGroupOverall.set(groupId, p);
                }
            } else {
                unique.push(p);
            }
        });

        const result: Product[] = [];

        if (currentSeason === 'all') {
            result.push(...unique);
            result.push(...Array.from(cheapestPerSeasonGroup.values()));
        } else {
            const seenGroupSeason = new Set<string>();
            cheapestPerSeasonGroup.forEach((prod, key) => {
                const [, groupId] = key.split('_');
                if (seenGroupSeason.has(groupId)) return;
                const seasonSpecificKey = `${currentSeason}_${groupId}`;
                if (cheapestPerSeasonGroup.has(seasonSpecificKey)) {
                    result.push(cheapestPerSeasonGroup.get(seasonSpecificKey)!);
                } else if (cheapestPerGroupOverall.has(groupId)) {
                    result.push(cheapestPerGroupOverall.get(groupId)!);
                }
                seenGroupSeason.add(groupId);
            });
            unique.forEach((u) => {
                if ((u.season ?? '').toLowerCase() === currentSeason.toLowerCase()) {
                    result.push(u);
                }
            });
        }

        return result;
    }, [products, currentSeason]);

    // const isLoadMoreVisible = groupedProducts.length > count;

    const sortedProducts = useMemo(() => {
        return [...groupedProducts].sort((a, b) => {
            if (sortOption === 'popular') {
                const priorityA = a.priority ?? 10;
                const priorityB = b.priority ?? 10;
                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }
                if (priorityA === 10) {
                    return 0;
                }
                const priceA = getEffectivePrice(a);
                const priceB = getEffectivePrice(b);
                if (priceA !== priceB) {
                    return priceA - priceB;
                }
                return 0;
            }
            const priceA = getEffectivePrice(a);
            const priceB = getEffectivePrice(b);
            return sortOption === 'expensive' ? priceB - priceA : priceA - priceB;
        });
    }, [groupedProducts, sortOption]);

    const productsToRender = useMemo(() => sortedProducts.slice(0, count), [sortedProducts, count]);
    const isLoadMoreVisible = sortedProducts.length > count;

    const handleSortOptionChange = useCallback((option: 'popular' | 'expensive' | 'cheap') => {
        setSortOption(option);
        setIsDropdownOpen(false);
        toggleRef.current?.focus();
    }, []);

    const toggleDropdown = () => {
        setIsDropdownOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!isDropdownOpen) return;
            const root = dropdownRootRef.current;
            if (root && !root.contains(e.target as Node)) setIsDropdownOpen(false);
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsDropdownOpen(false);
                toggleRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKey);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        setCount(PRODUCT_LIST_LIMIT);
        setSortOption('popular');
    }, [url]);

    const showMoreHandler = (nextCount: number) => {
        setCount((prev) => Math.max(prev, nextCount));
    };

    const sortLabel = sortOption === 'popular' ? 'По популярности' : sortOption === 'expensive' ? 'По убыванию цены' : 'По возрастанию цены';

    const listStyle = useMemo(() => {
        const s: React.CSSProperties & Record<string, string> = {
            '--pcl-columns': String(gridColumns),
        };
        return s;
    }, [gridColumns]);

    return (
        <div className={styles['product-card-list']}>
            <div className={styles['list-wrapper']}>
                {!isWishlist && (
                    <div className={styles['sort-wrapper']}>
                        <div className={styles['sort-controls']}>
                            <div ref={dropdownRootRef} className={styles.dropdown}>
                                <button
                                    ref={(el) => (toggleRef.current = el)}
                                    className={styles['dropdown-toggle']}
                                    aria-haspopup="listbox"
                                    aria-expanded={isDropdownOpen}
                                    aria-controls={dropdownPanelId}
                                    onClick={toggleDropdown}
                                >
                                    <span className={styles['dropdown-label']}>{sortLabel}</span>
                                    <img
                                        className={`${styles['dropdown-arrow']} ${isDropdownOpen ? styles.open : ''}`.trim()}
                                        src={isMobile ? '/icons/new/sort.svg' : '/icons/new/arrow_down.svg'}
                                        alt=""
                                        aria-hidden="true"
                                    />
                                </button>
                                <div
                                    id={dropdownPanelId}
                                    className={`${styles['dropdown-panel']} ${isDropdownOpen ? styles.open : ''}`.trim()}
                                    role="listbox"
                                    aria-label="Сортировка"
                                >
                                    <ul className={styles['dropdown-list']}>
                                        <li>
                                            <button
                                                type="button"
                                                className={`${styles['dropdown-item']} ${sortOption === 'popular' ? styles.active : ''}`.trim()}
                                                onClick={() => handleSortOptionChange('popular')}
                                            >
                                                По популярности
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className={`${styles['dropdown-item']} ${sortOption === 'expensive' ? styles.active : ''}`.trim()}
                                                onClick={() => handleSortOptionChange('expensive')}
                                            >
                                                По убыванию цены
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className={`${styles['dropdown-item']} ${sortOption === 'cheap' ? styles.active : ''}`.trim()}
                                                onClick={() => handleSortOptionChange('cheap')}
                                            >
                                                По возрастанию цены
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <ProductGridColumnsToggle
                                value={gridColumns}
                                onChange={setGridColumns}
                                options={toggleOptions}
                                variant={layoutMode}
                                ariaLabel="Колонки выдачи товаров"
                            />
                        </div>
                    </div>
                )}
                {!isWishlist && selectedFilterChips.length > 0 && (
                    <div className={styles['selected-filters']} aria-label="Выбранные фильтры">
                        <ul className={styles['selected-filters-list']}>
                            {selectedFilterChips.map((chip) => (
                                <li key={chip.key} className={styles['selected-filters-item']}>
                                    <button
                                        type="button"
                                        className={styles['selected-filter-chip']}
                                        onClick={chip.onRemove}
                                        aria-label={`Убрать фильтр «${chip.label}»`}
                                    >
                                        <span className={styles['selected-filter-chip-label']}>{chip.label}</span>
                                        <img className={styles['selected-filter-chip-icon']} src="/icons/new/close.svg" alt="" aria-hidden="true" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <CatalogDescription />
                <ul className={styles.list} style={listStyle}>
                    {productsToRender.map((product) => {
                        const cardData: ProductCardData = mapProductToCardData(product);
                        return <ProductCard key={`prod-${product.id}`} data={cardData} />;
                    })}
                </ul>
            </div>

            {isLoadMoreVisible && (
                <div className={styles['load-more-wrapper']}>
                    <LoadMore count={count} itemsListLength={sortedProducts.length} onClick={showMoreHandler} itemsLimit={PRODUCT_LIST_LIMIT} />
                </div>
            )}
        </div>
    );
};

export default ProductCardList;
